import type { SessionAttendee } from '@sunrise-ui/api/events/api';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { QueryKeys } from '../constants/QueryKeys';
import { getProfile } from '../queries/profiles';

const { profileKey } = QueryKeys.profiles;
/**
 * Custom hook to sort speakers alphabetically by first name
 * Handles bulk profile fetching and sorting logic
 */
export const useSortedSpeakersByFirstNameFromCache = (
  speakers: SessionAttendee[] = [],
) => {
  // Get all speaker user IDs
  const profileCacheQueries = useQueries({
    queries: speakers.map(({ user_id }) => ({
      queryKey: [profileKey, user_id],
      enabled: false,
      queryFn: () => getProfile(user_id),
    })),
  });

  const profileCacheMap = new Map(
    profileCacheQueries
      .map((query) => query.data)
      .map((profile) => [profile?.user_id, profile]),
  );

  // Sort speakers alphabetically by first name
  const sortedSpeakers = useMemo(() => {
    if (!speakers || profileCacheMap.size !== speakers.length) {
      return speakers;
    }

    return [...speakers].sort((a, b) => {
      const { user_id: userIdA } = a;
      const { user_id: userIdB } = b;
      const profileA = profileCacheMap.get(userIdA);
      const profileB = profileCacheMap.get(userIdB);

      const { first_name: firstNameA = '' } = profileA ?? {};
      const { first_name: firstNameB = '' } = profileB ?? {};

      return firstNameA.toLowerCase().localeCompare(firstNameB.toLowerCase());
    });
  }, [speakers, profileCacheMap]);

  return {
    sortedSpeakers,
  };
};
